import { computed, ref, unref, watch, type MaybeRef, type Ref } from 'vue';
import type { RdoScheduleActivity } from '@/modules/field/types/rdo-form';
import {
  filterScheduleActivities,
  uniqueFilterValues,
  type RdoActivityFilters,
} from '@/modules/field/composables/useRdoScheduleActivities';
import type {
  FieldReportFilterOptions,
  FieldReportFilters,
  FieldReportListItem,
} from '@/modules/field/types';
import { sortFieldReportsChronologically } from '@/modules/field/utils/fieldReportListSort';
import { resolveFieldReportDeadlineStatus } from '@/modules/field/utils/fieldReportDeadline';
import { withoutCancelledFieldReports } from '@/modules/field/utils/fieldReportCancelled';

/** Situações padrão na listagem (Pendente e Recusado). */
export const DEFAULT_REPORT_SITUATIONS: string[] = [
  'pending',
  'draft',
  'recused',
];

export function parseListParam(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value
      .map((entry) => String(entry).trim())
      .filter((entry) => entry !== '');
  }

  if (typeof value === 'string' && value.trim() !== '') {
    return value
      .split(',')
      .map((entry) => entry.trim())
      .filter((entry) => entry !== '');
  }

  return [];
}

/** Garante que "Pendente" inclua rascunhos (mesmo critério do resumo da home). */
export function expandPendingWithDraft(situations: string[]): string[] {
  const next = new Set(situations);

  if (next.has('pending')) {
    next.add('draft');
  }

  return Array.from(next);
}

export function resolveReportSituations(applied: unknown): string[] {
  const parsed = parseListParam(applied);

  if (parsed.length === 0) {
    return [...DEFAULT_REPORT_SITUATIONS];
  }

  return expandPendingWithDraft(parsed);
}

export function isReportSituationSelected(
  selectedSituations: string[],
  value: string,
): boolean {
  if (value === 'pending') {
    return (
      selectedSituations.includes('pending') ||
      selectedSituations.includes('draft')
    );
  }

  return selectedSituations.includes(value);
}

export function toggleReportSituation(
  current: string[],
  value: string,
  checked: boolean,
): string[] {
  const next = new Set(current);

  if (value === 'pending') {
    if (checked) {
      next.add('pending');
      next.add('draft');
    } else {
      next.delete('pending');
      next.delete('draft');
    }
  } else if (checked) {
    next.add(value);
  } else {
    next.delete(value);
  }

  return Array.from(next);
}

export const DEFAULT_FIELD_REPORT_FILTERS: FieldReportFilters = {
  clientId: '',
  divisionId: '',
  projectId: '',
  type: '',
  stage: '',
  floor: '',
  system: '',
  device: '',
  myCollaborator: false,
  situation: [...DEFAULT_REPORT_SITUATIONS],
  deadlineStatus: [],
  dateFrom: '',
  dateTo: '',
};

export function syncFiltersFromApplied(
  applied: Partial<FieldReportFilters> | undefined,
  filters: FieldReportFilters,
): void {
  filters.clientId = applied?.clientId ? String(applied.clientId) : '';
  filters.divisionId = applied?.divisionId ? String(applied.divisionId) : '';
  filters.projectId = applied?.projectId ? String(applied.projectId) : '';
  filters.type = applied?.type ?? '';
  filters.stage = applied?.stage ?? '';
  filters.floor = applied?.floor ?? '';
  filters.system = applied?.system ?? '';
  filters.device = applied?.device ?? '';
  filters.situation = resolveReportSituations(applied?.situation);
  filters.deadlineStatus = parseListParam(applied?.deadlineStatus);
  filters.dateFrom = applied?.dateFrom ?? '';
  filters.dateTo = applied?.dateTo ?? '';
  filters.myCollaborator = Boolean(applied?.myCollaborator);
}

function reportMatchesSituationFilter(
  reportSituation: string,
  selectedSituations: string[],
): boolean {
  if (selectedSituations.length === 0) {
    return true;
  }

  const selected = new Set(expandPendingWithDraft(selectedSituations));

  return selected.has(reportSituation);
}

function reportMatchesServerSideFilters(
  report: FieldReportListItem,
  filters: FieldReportFilters,
  currentUserId: number | null,
): boolean {
  if (filters.clientId) {
    const clientId = Number(filters.clientId);
    if (report.clientId !== clientId) {
      return false;
    }
  }

  if (filters.divisionId) {
    const divisionId = Number(filters.divisionId);
    if (report.divisionId !== divisionId) {
      return false;
    }
  }

  if (filters.projectId) {
    const projectId = Number(filters.projectId);
    if (report.projectId !== projectId) {
      return false;
    }
  }

  if (filters.type && report.type !== filters.type) {
    return false;
  }

  if (
    filters.situation.length > 0 &&
    !reportMatchesSituationFilter(report.situation, filters.situation)
  ) {
    return false;
  }

  if (filters.deadlineStatus.length > 0) {
    const selected = new Set(filters.deadlineStatus);
    const status = resolveFieldReportDeadlineStatus(report.reportDateIso);
    if (!selected.has(status)) {
      return false;
    }
  }

  if (filters.dateFrom && report.reportDateIso < filters.dateFrom) {
    return false;
  }

  if (filters.dateTo && report.reportDateIso > filters.dateTo) {
    return false;
  }

  if (filters.myCollaborator && currentUserId) {
    const uid = currentUserId;
    if (report.createdBy !== uid && !report.collaboratorUserIds.includes(uid)) {
      return false;
    }
  }

  return true;
}

function arraysEqualUnordered(a: string[], b: string[]): boolean {
  if (a.length !== b.length) {
    return false;
  }

  const sortedA = [...a].sort();
  const sortedB = [...b].sort();

  return sortedA.every((value, index) => value === sortedB[index]);
}

export type UseFieldReportFiltersOptions = {
  /**
   * Quando true, relatórios sincronizados já vêm filtrados pelo servidor (Inertia).
   * Use um computed/ref — ex.: só confiar no servidor quando há props.reports.
   */
  serverFiltersApplied?: MaybeRef<boolean>;
};

export function useFieldReportFilters(
  reports: Ref<FieldReportListItem[]>,
  search: Ref<string>,
  filters: FieldReportFilters,
  filterOptions: Ref<FieldReportFilterOptions>,
  currentUserId: Ref<number | null>,
  options: UseFieldReportFiltersOptions = {},
) {
  const trustServerForSynced = computed(
    () => unref(options.serverFiltersApplied) ?? false,
  );
  const scheduleActivities = ref<RdoScheduleActivity[]>([]);
  const scheduleLoading = ref(false);

  const projectsForClient = computed(() => {
    if (!filters.clientId) {
      return filterOptions.value.projects;
    }

    const clientId = Number(filters.clientId);

    return filterOptions.value.projects.filter(
      (p) => p.clientId === clientId,
    );
  });

  const hierarchyFilters = computed<RdoActivityFilters>(() => ({
    stage: filters.stage,
    floor: filters.floor,
    vertical: '',
    system: filters.system,
    device: filters.device,
    plannedFrom: '',
    plannedTo: '',
    responsible: '',
    situation: '',
    onlyToday: false,
  }));

  const matchingScheduleItemIds = computed(() => {
    if (!filters.projectId) {
      return null;
    }

    const hasHierarchy = Boolean(
      filters.stage ||
        filters.floor ||
        filters.system ||
        filters.device,
    );

    if (!hasHierarchy) {
      return null;
    }

    if (!scheduleActivities.value.length) {
      return new Set<number>();
    }

    const matched = filterScheduleActivities(
      scheduleActivities.value,
      hierarchyFilters.value,
    );

    return new Set(matched.map((a) => a.id));
  });

  const stageOptions = computed(() =>
    uniqueFilterValues(scheduleActivities.value, 'stage'),
  );
  const floorOptions = computed(() =>
    uniqueFilterValues(scheduleActivities.value, 'floor'),
  );
  const systemOptions = computed(() =>
    uniqueFilterValues(scheduleActivities.value, 'system'),
  );
  const deviceOptions = computed(() =>
    uniqueFilterValues(scheduleActivities.value, 'device'),
  );

  const projectHierarchyEnabled = computed(() => Boolean(filters.projectId));

  async function loadScheduleForProject(projectId: string) {
    if (!projectId) {
      scheduleActivities.value = [];
      return;
    }

    scheduleLoading.value = true;

    try {
      const response = await fetch(`/field/rdo/schedule-tree/${projectId}`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      });

      if (!response.ok) {
        scheduleActivities.value = [];
        return;
      }

      const data = (await response.json()) as { activities?: RdoScheduleActivity[] };
      scheduleActivities.value = data.activities ?? [];
    } catch {
      scheduleActivities.value = [];
    } finally {
      scheduleLoading.value = false;
    }
  }

  watch(
    () => filters.projectId,
    (projectId) => {
      filters.stage = '';
      filters.floor = '';
      filters.system = '';
      filters.device = '';
      void loadScheduleForProject(projectId);
    },
  );

  watch(
    () => filters.clientId,
    () => {
      if (!filters.projectId) {
        return;
      }

      const stillValid = projectsForClient.value.some(
        (p) => String(p.id) === filters.projectId,
      );

      if (!stillValid) {
        filters.projectId = '';
      }
    },
  );

  const filteredReports = computed(() => {
    let list = withoutCancelledFieldReports([...reports.value]);
    const uid = currentUserId.value;

    list = list.filter((report) => {
      if (trustServerForSynced.value && report.sync === 'synced') {
        return true;
      }

      return reportMatchesServerSideFilters(report, filters, uid);
    });

    const scheduleIds = matchingScheduleItemIds.value;
    if (scheduleIds) {
      list = list.filter((r) =>
        r.activityScheduleItemIds.some((id) => scheduleIds.has(id)),
      );
    }

    const q = search.value.trim().toLowerCase();
    if (q) {
      list = list.filter((report) => {
        const haystack = [
          report.title,
          report.client,
          report.division,
          report.reportType,
          report.date,
        ]
          .join(' ')
          .toLowerCase();

        return haystack.includes(q);
      });
    }

    return sortFieldReportsChronologically(list);
  });

  const activeFilterCount = computed(() => {
    let count = 0;
    if (filters.clientId) count += 1;
    if (filters.divisionId) count += 1;
    if (filters.projectId) count += 1;
    if (filters.type) count += 1;
    if (filters.stage) count += 1;
    if (filters.floor) count += 1;
    if (filters.system) count += 1;
    if (filters.device) count += 1;
    if (filters.myCollaborator) count += 1;
    if (
      filters.situation.length > 0 &&
      !arraysEqualUnordered(filters.situation, DEFAULT_REPORT_SITUATIONS)
    ) {
      count += 1;
    }
    if (filters.deadlineStatus.length > 0) count += 1;
    if (filters.dateFrom || filters.dateTo) count += 1;
    return count;
  });

  function clearFilters() {
    Object.assign(filters, {
      ...DEFAULT_FIELD_REPORT_FILTERS,
      situation: [...DEFAULT_REPORT_SITUATIONS],
      deadlineStatus: [],
    });
  }

  function filtersToQueryParams(): Record<string, string | number> {
    const params: Record<string, string | number> = {};

    if (filters.clientId) params.client_id = Number(filters.clientId);
    if (filters.divisionId) params.division_id = Number(filters.divisionId);
    if (filters.projectId) params.project_id = Number(filters.projectId);
    if (filters.type) params.type = filters.type;

    const situations = expandPendingWithDraft(filters.situation);
    if (situations.length > 0) {
      params.situation = situations.join(',');
    }

    if (filters.deadlineStatus.length > 0) {
      params.deadline_status = filters.deadlineStatus.join(',');
    }
    if (filters.dateFrom) params.date_from = filters.dateFrom;
    if (filters.dateTo) params.date_to = filters.dateTo;
    if (filters.myCollaborator) params.my_collaborator = 1;
    if (filters.stage) params.stage = filters.stage;
    if (filters.floor) params.floor = filters.floor;
    if (filters.system) params.system = filters.system;
    if (filters.device) params.device = filters.device;

    return params;
  }

  return {
    filteredReports,
    projectsForClient,
    stageOptions,
    floorOptions,
    systemOptions,
    deviceOptions,
    projectHierarchyEnabled,
    scheduleLoading,
    activeFilterCount,
    clearFilters,
    filtersToQueryParams,
    loadScheduleForProject,
  };
}
